import { useSyncExternalStore } from 'react';

const defaultImportes = {
  neto: 0,
  iva21: 0,
  iva105: 0,
  iva27: 0,
  exento: 0,
  noGravado: 0,
  percepIva: 0,
  percepIibb: 0,
  otros: 0,
  total: 0,
};

const defaultState = {
  supplier: null,
  comprobante: {
    tipo: 'Factura A',
    puntoVenta: '0001',
    numero: '',
    fecha: new Date().toISOString().slice(0, 10),
    cae: '',
    caeVto: '',
    moneda: 'ARS',
    condicionCompra: 'Contado',
    observaciones: '',
  },
  importes: { ...defaultImportes },
  items: [],
  attachments: [],
  ocr: {
    status: 'idle',
    progress: 0,
    suggestions: null,
    logs: [],
  },
};

let state = structuredClone(defaultState);
const listeners = new Set();

function notify() {
  listeners.forEach(listener => listener());
}

function setState(updater) {
  const nextState = typeof updater === 'function' ? updater(state) : { ...state, ...updater };
  state = nextState;
  notify();
}

function structuredClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function generateId(prefix = 'id') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function mergeImportes(partial) {
  return { ...state.importes, ...partial };
}

function updateItem(id, partial) {
  const items = state.items.map(item => (item.id === id ? { ...item, ...partial } : item));
  setState(prev => ({ ...prev, items }));
}

function ensureNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (!value) return 0;
  const normalized = value.toString().replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function recalcTotals(importes) {
  const base = { ...defaultImportes, ...importes };
  const totalCalculado =
    ensureNumber(base.neto) +
    ensureNumber(base.iva21) +
    ensureNumber(base.iva105) +
    ensureNumber(base.iva27) +
    ensureNumber(base.exento) +
    ensureNumber(base.noGravado) +
    ensureNumber(base.percepIva) +
    ensureNumber(base.percepIibb) +
    ensureNumber(base.otros);
  return { ...base, totalCalculado };
}

export function getPurchaseFormState() {
  return state;
}

export function resetPurchaseFormState() {
  state = structuredClone(defaultState);
  notify();
}

export function purchaseFormActions() {
  return {
    setSupplier(supplier) {
      setState(prev => ({ ...prev, supplier }));
    },
    setComprobanteField(name, value) {
      setState(prev => ({
        ...prev,
        comprobante: {
          ...prev.comprobante,
          [name]: value,
        },
      }));
    },
    setObservaciones(value) {
      setState(prev => ({
        ...prev,
        comprobante: {
          ...prev.comprobante,
          observaciones: value,
        },
      }));
    },
    setImporte(name, value) {
      const importes = mergeImportes({ [name]: ensureNumber(value) });
      const totals = recalcTotals(importes);
      setState(prev => ({ ...prev, importes: { ...importes, totalCalculado: totals.totalCalculado } }));
    },
    recalcImportes() {
      const totals = recalcTotals(state.importes);
      setState(prev => ({ ...prev, importes: { ...prev.importes, totalCalculado: totals.totalCalculado } }));
    },
    setTotal(value) {
      const total = ensureNumber(value);
      setState(prev => ({ ...prev, importes: { ...prev.importes, total, totalCalculado: recalcTotals(prev.importes).totalCalculado } }));
    },
    addItem(item = {}) {
      const newItem = {
        id: generateId('item'),
        codigo: '',
        descripcion: '',
        cantidad: 1,
        precio: 0,
        alicuotaIva: 21,
        subtotal: 0,
        ...item,
      };
      setState(prev => ({ ...prev, items: [...prev.items, newItem] }));
    },
    updateItem,
    removeItem(id) {
      setState(prev => ({ ...prev, items: prev.items.filter(item => item.id !== id) }));
    },
    setItems(items) {
      setState(prev => ({ ...prev, items: items.map(item => ({ id: item.id || generateId('item'), ...item })) }));
    },
    addAttachments(files) {
      const attachments = files.map(file => ({
        id: generateId('att'),
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        file,
      }));
      setState(prev => ({ ...prev, attachments: [...prev.attachments, ...attachments] }));
    },
    removeAttachment(id) {
      setState(prev => ({ ...prev, attachments: prev.attachments.filter(file => file.id !== id) }));
    },
    setAttachments(list) {
      setState(prev => ({ ...prev, attachments: list.map(file => ({ id: file.id || generateId('att'), ...file })) }));
    },
    setOcrState(partial) {
      setState(prev => ({ ...prev, ocr: { ...prev.ocr, ...partial } }));
    },
    applyOcrSuggestions(values) {
      if (!values) return;
      setState(prev => ({
        ...prev,
        supplier: values.supplier || prev.supplier,
        comprobante: {
          ...prev.comprobante,
          tipo: values.tipo || prev.comprobante.tipo,
          puntoVenta: values.puntoVenta || prev.comprobante.puntoVenta,
          numero: values.numero || prev.comprobante.numero,
          fecha: values.fecha || prev.comprobante.fecha,
          cae: values.cae ?? prev.comprobante.cae,
          caeVto: values.caeVto ?? prev.comprobante.caeVto,
          moneda: values.moneda || prev.comprobante.moneda,
          condicionCompra: values.condicionCompra || prev.comprobante.condicionCompra,
        },
        importes: {
          ...prev.importes,
          neto: values.neto ?? prev.importes.neto,
          iva21: values.iva21 ?? prev.importes.iva21,
          iva105: values.iva105 ?? prev.importes.iva105,
          iva27: values.iva27 ?? prev.importes.iva27,
          exento: values.exento ?? prev.importes.exento,
          noGravado: values.noGravado ?? prev.importes.noGravado,
          percepIva: values.percepIva ?? prev.importes.percepIva,
          percepIibb: values.percepIibb ?? prev.importes.percepIibb,
          otros: values.otros ?? prev.importes.otros,
          total: values.total ?? prev.importes.total,
        },
      }));
    },
  };
}

export function usePurchaseForm(selector = state => state) {
  return useSyncExternalStore(
    listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => selector(state),
    () => selector(state),
  );
}
